import React from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Package, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";

const PurchaseOrderStatusChart = ({ data, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Estado de Órdenes de Compra</CardTitle>
          <CardDescription>Cargando datos...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Estado de Órdenes de Compra</CardTitle>
          <CardDescription>No hay datos disponibles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            <p>No hay órdenes de compra disponibles</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Procesar datos para el gráfico de estado
  const statusCounts = data.reduce((acc, po) => {
    const status = po.status || "pending";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const statusLabels = {
    pending: "Pendiente",
    sent: "Enviada",
    confirmed: "Confirmada",
    received: "Recibida",
    cancelled: "Cancelada",
    completed: "Completada"
  };

  const statusColors = {
    pending: "#f59e0b",
    sent: "#3b82f6",
    confirmed: "#8b5cf6",
    received: "#10b981",
    cancelled: "#ef4444",
    completed: "#059669"
  };

  const pieData = Object.entries(statusCounts).map(([status, count]) => ({
    name: statusLabels[status] || status,
    value: count,
    status: status,
    color: statusColors[status] || "#6b7280"
  }));

  // Procesar datos por mes
  const monthlyData = {};
  data.forEach((po) => {
    if (po.created_at) {
      const month = new Date(po.created_at).toLocaleDateString("es-ES", { month: "short" });
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          total: 0,
          confirmed: 0,
          received: 0,
          cancelled: 0
        };
      }
      monthlyData[month].total += 1;
      if (po.status === "confirmed" || po.status === "received") {
        monthlyData[month].confirmed += 1;
      }
      if (po.status === "received") {
        monthlyData[month].received += 1;
      }
      if (po.status === "cancelled") {
        monthlyData[month].cancelled += 1;
      }
    }
  });

  const monthlyChartData = Object.values(monthlyData).sort((a, b) => {
    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return months.indexOf(a.month) - months.indexOf(b.month);
  });

  // Calcular estadísticas generales
  const totalPOs = data.length;
  const confirmedPOs = data.filter(po => po.status === "confirmed" || po.status === "received").length;
  const cancelledPOs = data.filter(po => po.status === "cancelled").length;
  const completionRate = totalPOs > 0 ? (confirmedPOs / totalPOs) * 100 : 0;

  
  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "sent":
      case "confirmed":
        return <CheckCircle className="h-4 w-4" />;
      case "received":
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "cancelled":
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  return (
    <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              Estado de Órdenes de Compra
            </CardTitle>
            <CardDescription className="text-slate-600 dark:text-slate-400">
              Distribución por estado y tendencia mensual
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Estadísticas generales */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Total POs
              </span>
            </div>
            <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
              {totalPOs}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                Confirmadas
              </span>
            </div>
            <p className="text-lg font-bold text-green-700 dark:text-green-300">
              {confirmedPOs}
            </p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                Tasa Completitud
              </span>
            </div>
            <p className="text-lg font-bold text-orange-700 dark:text-orange-300">
              {Math.round(completionRate)}%
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de pastel - Estado */}
          <div className="h-80">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Distribución por Estado
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: data.color }}
                            ></div>
                            <p className="font-semibold text-slate-900 dark:text-slate-100">
                              {data.name}
                            </p>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Cantidad: {data.value} POs
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Porcentaje: {Math.round((data.value / totalPOs) * 100)}%
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfico de barras - Tendencia mensual */}
          <div className="h-80">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Tendencia Mensual
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                          <p className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            {data.month}
                          </p>
                          <div className="space-y-1">
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Total:</span> {data.total} POs
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Confirmadas:</span> {data.confirmed} POs
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Recibidas:</span> {data.received} POs
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Canceladas:</span> {data.cancelled} POs
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="total" name="Total POs" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="confirmed" name="Confirmadas" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="received" name="Recibidas" fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cancelled" name="Canceladas" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Lista de estados con iconos */}
        <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            Resumen por Estado
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {pieData.map((item, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded-lg">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                ></div>
                <div className="flex items-center gap-1">
                  {getStatusIcon(item.status)}
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {item.name}
                  </span>
                </div>
                <span className="text-sm font-bold text-slate-900 dark:text-slate-100 ml-auto">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PurchaseOrderStatusChart;
